import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Stepper } from 'src/design/molecules/Stepper';
import { useFormik } from 'formik';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import {
  formatWorkWeekId,
  getWorkWeek,
  parseWorkWeekId,
} from 'src/utils/workWeek';
import { useNavigate } from 'react-router-dom';
import { tempOptions } from 'src/routes/Jobs/Timesheets/components/WeekSelector';
import * as Yup from 'yup';

import {
  DailyDto,
  DailyQuestionDto,
  DailyRequestDto,
  JobDto,
  JobUserDto,
  TimesheetDto,
} from 'src/api/FiretrolApiClient';
import { useJobTeam } from 'src/api/queries/jobs/useJobTeam';
import { useOfficeByDivision } from 'src/api/queries/organization/useOfficeByDivision';
import { useDaily } from 'src/api/queries/dailys/useDaily';
import { useQuestions } from 'src/api/queries/questions/useQuestions';
import { useDailys } from 'src/api/queries/dailys/useDailys';
import { useDailyDesignProgressSummary } from 'src/api/queries/dailys/useDailyDesignProgressSummary';
import {
  useCreateDaily,
  useUpdateDaily,
} from 'src/api/queries/dailys/useDailyMutations';
import { useTimesheetsForJob } from 'src/api/queries/timesheets/useTimesheetsForJob';
import { useJobs } from 'src/hooks/useJobs';
import {
  getErrorMessage,
  logger,
  showErrorToast,
  showSuccessToast,
  formatDate,
} from 'src/utils';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { applyAttachmentChange } from 'src/design/organisms/attachments/attachmentEdits';
import { IDateOption } from 'src/routes/Jobs/Timesheets/types';
import { DescriptionStep } from './steps/DescriptionStep';
import { JobStep } from './steps/JobStep';
import { PhotosStep } from './steps/PhotosStep';
import { ProgressStep } from './steps/ProgressStep';
import { QuestionsStep } from './steps/QuestionsStep';
import { SubmitStep } from './steps/SubmitStep';
import { TimesheetsStep } from './steps/TimesheetsStep';
import { buildDailyQuestionAnswers } from './questionAnswers';
import { DailyStep, DailyStepMap } from './steps/types';

export const DAILY_DESCRIPTION_MAX_LENGTH = 1000;

export const validationSchema = Yup.object().shape({
  date: Yup.date().required(),
  jobId: Yup.number().required(),
  description: Yup.string().max(
    DAILY_DESCRIPTION_MAX_LENGTH,
    `Description must be ${DAILY_DESCRIPTION_MAX_LENGTH} characters or fewer`,
  ),
  weather: Yup.string().required(),
  questions: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().required(),
      question: Yup.string().required(),
      note: Yup.string().required(),
      value: Yup.string().required(),
    }),
  ),
  progressItemRequests: Yup.array().of(
    Yup.object().shape({
      value: Yup.number().required(),
      wbsAccountId: Yup.string().required(),
    }),
  ),

  excludeImages: Yup.boolean().required(),
  excludeQuestions: Yup.boolean().required(),
  excludeProgress: Yup.boolean().required(),
  excludeTimesheets: Yup.boolean().required(),
  exclusionImagesReason: Yup.string().max(1000),
  exclusionQuestionsReason: Yup.string().max(1000),
  exclusionProgressReason: Yup.string()
    .max(1000)
    .when('excludeProgress', {
      is: true,
      then: (schema) => schema.required('Reason for exclusion is required'),
    }),
  exclusionTimesheetsReason: Yup.string()
    .max(1000)
    .when('excludeTimesheets', {
      is: true,
      then: (schema) => schema.required('Reason for exclusion is required'),
    }),
});

interface IDailyProps {
  initialValues: DailyDto;
  dailyId?: string;
  initialJob: JobDto;
}

const EMPTY_TIMESHEETS: TimesheetDto[] = [];
const EMPTY_TEAM: JobUserDto[] = [];
const EMPTY_DAILYS: DailyDto[] = [];
const EMPTY_JOB_QUESTIONS: DailyQuestionDto[] = [];
const EMPTY_PROGRESS_ITEM_IDS: string[] = [];

export const buildDailyRequest = (
  values: DailyDto,
  editingDailyId: string | null,
): DailyRequestDto => {
  const { progressItemIds, ...rest } = values;
  const request: DailyRequestDto = {
    ...rest,
    progressItemRequests: values.progressItemRequests?.filter(
      (pi) => pi.value > 0,
    ),
    progressItemIds: progressItemIds ?? [],
  };
  return editingDailyId ? { ...request, id: editingDailyId } : request;
};

export const DailyForm: React.FC<IDailyProps> = ({
  initialValues,
  initialJob,
  dailyId,
}) => {
  const { jobs } = useJobs();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedJobOverride, setSelectedJobOverride] = useState<JobDto | null>(
    null,
  );
  const [updatedTimesheets, setUpdatedTimesheets] = useState<TimesheetDto[]>(
    [],
  );
  const [selectedWeek, setSelectedWeek] = useState<IDateOption | null>(() => {
    const { weekNumber, year } = getWorkWeek(initialValues.date);
    return (
      tempOptions.find((o) => o.id === formatWorkWeekId(year, weekNumber)) ||
      null
    );
  });
  const [checkDate, setCheckDate] = useState<Date | null>(
    initialValues.date ?? null,
  );
  const [editingDailyId, setEditingDailyId] = useState<string | null>(
    dailyId || null,
  );
  const [loadExistingRequested, setLoadExistingRequested] = useState(false);

  const selectedJob: JobDto | null = selectedJobOverride ?? initialJob ?? null;

  const { data: office } = useOfficeByDivision(selectedJob?.divisionId);
  const { data: jobQuestions = EMPTY_JOB_QUESTIONS } = useQuestions(
    selectedJob?.id,
  );

  const selectedWeekParts = selectedWeek
    ? parseWorkWeekId(selectedWeek.id)
    : null;
  const { data: timesheets = EMPTY_TIMESHEETS, isLoading: timesheetsLoading } =
    useTimesheetsForJob(
      selectedJob?.id,
      {
        weekId: selectedWeekParts?.weekNumber,
        year: selectedWeekParts?.year,
        dailyContext: true,
      },
      { enabled: !!selectedWeekParts },
    );

  const { data: team = EMPTY_TEAM, isLoading: teamLoading } = useJobTeam(
    selectedJob?.id,
  );

  // A daily already filed for the chosen job and day blocks a second one and
  // offers to load it for editing instead.
  const checkDateOnly = React.useMemo(
    () => (checkDate ? moment(checkDate).startOf('day').toDate() : null),
    [checkDate],
  );
  const { data: sameDayDailies = EMPTY_DAILYS } = useDailys(
    {
      jobId: selectedJob?.id,
      startDate: checkDateOnly ?? undefined,
      endDate: checkDateOnly ?? undefined,
    },
    { enabled: !editingDailyId && !!selectedJob && !!checkDateOnly },
  );
  const existingDaily =
    !editingDailyId && checkDateOnly
      ? sameDayDailies.find((d) =>
          moment(d.date).isSame(checkDateOnly, 'day'),
        ) ?? null
      : null;

  const { data: fullExistingDaily } = useDaily(
    loadExistingRequested ? existingDaily?.id : undefined,
  );

  const createDaily = useCreateDaily(selectedJob?.id ?? 0);
  const updateDaily = useUpdateDaily(selectedJob?.id ?? 0);
  const saving = createDaily.isPending || updateDaily.isPending;

  React.useEffect(() => {
    if (initialJob) {
      formikRef.current.setFieldValue('jobId', initialJob.id);
    } else {
      formikRef.current.setFieldValue('questions', []);
    }
  }, [initialJob]);

  // Only apply office-setting defaults to fields the user hasn't already
  // interacted with - otherwise a slow response can silently clobber a
  // switch the user toggled while it was loading.
  React.useEffect(() => {
    if (!office) return;
    const { touched, setFieldValue: setValue } = formikRef.current;

    if (!touched.excludeProgress) {
      setValue('excludeProgress', office.dailyModules.progress === false);
    }
    if (!touched.excludeImages) {
      setValue('excludeImages', office.dailyModules.media === false);
    }
    if (!touched.excludeTimesheets) {
      setValue('excludeTimesheets', office.dailyModules.timesheets === false);
    }
    if (!touched.excludeQuestions) {
      setValue('excludeQuestions', office.dailyModules.questions === false);
    }
    if (!touched.excludeTickets) {
      setValue('excludeTickets', office.dailyModules.tickets === false);
    }
  }, [office]);

  React.useEffect(() => {
    formikRef.current.setFieldValue(
      'questions',
      buildDailyQuestionAnswers(office?.dailyQuestions, jobQuestions),
    );
  }, [office, jobQuestions]);

  React.useEffect(() => {
    if (!loadExistingRequested || !fullExistingDaily) return;
    const fullDaily = fullExistingDaily;
    const formikInstance = formikRef.current;
    formikInstance.setValues({
      ...formikInstance.values,
      date: fullDaily.date ? new Date(fullDaily.date) : new Date(),
      description: fullDaily.description || '',
      weather: fullDaily.weather || '',
      questions: fullDaily.questions || [],
      status: fullDaily.status || 'draft',
      excludeImages: fullDaily.excludeImages || false,
      excludeQuestions: fullDaily.excludeQuestions || false,
      excludeProgress: fullDaily.excludeProgress || false,
      excludeTimesheets: fullDaily.excludeTimesheets || false,
      exclusionImagesReason: fullDaily.exclusionImagesReason || '',
      exclusionQuestionsReason: fullDaily.exclusionQuestionsReason || '',
      exclusionProgressReason: fullDaily.exclusionProgressReason || '',
      exclusionTimesheetsReason: fullDaily.exclusionTimesheetsReason || '',
      attachment: fullDaily.attachment || [],
      progressItemRequests:
        fullDaily.dailyProgressItem?.map((p) => ({
          wbsAccountId: p.wbsAccountId,
          value: p.value || 0,
        })) || [],
      progressItemIds: [],
    });

    setEditingDailyId(fullDaily.id);
    setLoadExistingRequested(false);
    showSuccessToast('Loaded existing daily report for editing');
  }, [loadExistingRequested, fullExistingDaily]);

  const loadExistingDaily = () => setLoadExistingRequested(true);

  const handleNext = () => {
    if (isLastStep) {
      saveDaily();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => {
    setActiveStep(step);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async () => {
      logger.debug('Daily form submitted');
    },
  });

  // Latest-ref mirror of `formik`, kept in sync every render so async
  // callbacks defined earlier in this closure (e.g. loadDailyOfficeSettings)
  // can read fresh touched/values instead of the stale snapshot they closed
  // over at mount.
  const formikRef = React.useRef(formik);
  formikRef.current = formik;

  const { values, setFieldValue, setFieldTouched, errors, touched } = formik;

  const questionsComplete = useMemo(() => {
    if (selectedJob === null) return false;
    const target = values.questions.find((q) => q.value === 'none');
    const comp = values.questions.find(
      (q) => q.value === 'yes' && q.note.length === 0,
    );
    return !target && !comp;
  }, [values.questions, selectedJob]);

  const progressItemIds = values.progressItemIds ?? EMPTY_PROGRESS_ITEM_IDS;
  const designProgressRequest = useMemo(
    () => ({ dailyId: editingDailyId ?? undefined, progressItemIds }),
    [editingDailyId, progressItemIds],
  );
  const { data: designProgressSummary } = useDailyDesignProgressSummary(
    selectedJob?.id,
    designProgressRequest,
  );
  const designProgressAccountCount = designProgressSummary?.length ?? 0;

  // Two saves can resolve before Formik re-renders, so ids accumulate through
  // a ref rather than the possibly stale values snapshot.
  const progressItemIdsRef = React.useRef(progressItemIds);
  progressItemIdsRef.current = progressItemIds;
  const onStatusesSaved = React.useCallback((savedIds: string[]) => {
    const existing = progressItemIdsRef.current;
    const merged = Array.from(new Set([...existing, ...savedIds]));
    if (merged.length === existing.length) return;
    progressItemIdsRef.current = merged;
    formikRef.current.setFieldValue('progressItemIds', merged);
  }, []);

  const progressComplete = useMemo(() => {
    if (selectedJob === null) return false;
    const target = values.progressItemRequests.find((q) => q.value > 0);
    return !!target || designProgressAccountCount > 0;
  }, [values.progressItemRequests, selectedJob, designProgressAccountCount]);

  const getStepCompletionStatus = (step: DailyStep) => {
    switch (step) {
      case 'job':
        return selectedJob !== null;
      case 'description':
        return !!values.date && values.description.length > 0;
      case 'photos':
        return (
          values.excludeImages ||
          values.attachmentsToAdd.length > 0 ||
          values.attachment.length > 0
        );
      case 'questions':
        return questionsComplete || values.excludeQuestions;
      case 'progress':
        return (
          progressComplete ||
          (values.excludeProgress &&
            !!values.exclusionProgressReason &&
            values.exclusionProgressReason.trim().length > 0)
        );
      case 'timesheets':
        return (
          updatedTimesheets.length > 0 ||
          (values.excludeTimesheets &&
            !!values.exclusionTimesheetsReason &&
            values.exclusionTimesheetsReason.trim().length > 0)
        );
      case 'submit':
        return Object.values(DailyStepMap)
          .filter((s) => s !== 'submit')
          .every((step) => getStepCompletionStatus(step));
      default:
        return false;
    }
  };

  const isAllStepsComplete = useMemo(() => {
    return Object.values(DailyStepMap)
      .filter((s) => s !== 'submit')
      .every((step) => getStepCompletionStatus(step));
  }, [getStepCompletionStatus]);

  const saveDaily = async () => {
    const data = buildDailyRequest(values, editingDailyId);

    try {
      if (editingDailyId) {
        await updateDaily.mutateAsync({ id: editingDailyId, request: data });
      } else {
        await createDaily.mutateAsync(data);
      }
    } catch {
      return;
    }

    navigate('/operations', { state: { job: selectedJob } });
  };

  const onJobSelected = (job: JobDto | null) => {
    if (job?.id !== selectedJob?.id) {
      // Progress rows and timesheet drafts are keyed to the previous job's WBS
      // and team, so they cannot carry over to a different job.
      setFieldValue('progressItemRequests', []);
      setFieldValue('progressItemIds', []);
      setUpdatedTimesheets([]);
    }
    setSelectedJobOverride(job);
    setCheckDate(job && values.date ? values.date : null);
    handleNext();
  };

  const onTimesheetChanged = (timesheet: TimesheetDto) => {
    // If the timesheet is already in the list, update it, otherwise add it
    setUpdatedTimesheets((prevState) => {
      const existing = prevState.find((t) => t.id === timesheet.id);
      if (existing) {
        return prevState.map((t) => (t.id === timesheet.id ? timesheet : t));
      }
      return [...prevState, timesheet];
    });
  };

  const onDateChange = (date: Date | null) => {
    setFieldValue('date', date);
    const { weekNumber, year } = getWorkWeek(date ?? new Date());
    const week = tempOptions.find(
      (o) => o.id === formatWorkWeekId(year, weekNumber),
    );
    if (week) {
      setSelectedWeek(week);
    }
    setCheckDate(selectedJob && date ? date : null);
  };

  const stepMap = useMemo(() => {
    return {
      ['job']: {
        label: 'Select Job',
        sublabel:
          selectedJob !== null
            ? `${selectedJob.jobNumber} - ${selectedJob.name}`
            : 'No Job Selected',
        component: (
          <JobStep
            jobs={jobs}
            selectedJob={selectedJob}
            onJobSelect={onJobSelected}
            values={values}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            locked={!!editingDailyId}
          />
        ),
      },
      ['description']: {
        label: 'Activities',
        sublabel: null,
        component: (
          <DescriptionStep
            values={values}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            selectedJob={selectedJob}
            onDateChange={onDateChange}
          />
        ),
      },
      ['photos']: {
        label: 'Photos',
        sublabel: null,
        component: (
          <PhotosStep
            values={values}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            selectedJob={selectedJob}
            onAttachmentsChange={(files, change) => {
              const edits = applyAttachmentChange(
                {
                  added: values.attachmentsToAdd ?? [],
                  removed: values.attachmentsToRemove ?? [],
                },
                change,
              );
              setFieldValue('attachment', files);
              setFieldValue('attachmentsToAdd', edits.added);
              setFieldValue('attachmentsToRemove', edits.removed);
            }}
          />
        ),
      },
      ['questions']: {
        label: 'Daily Questions',
        sublabel: null,
        component: (
          <QuestionsStep
            values={values}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            selectedJob={selectedJob}
            onQuestionsChange={(q) => setFieldValue('questions', q)}
          />
        ),
      },
      ['progress']: {
        label: 'Progress',
        sublabel: null,
        component: (
          <ProgressStep
            values={values}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            selectedJob={selectedJob}
            onProgressChange={(pi) => setFieldValue('progressItemRequests', pi)}
            designProgressRequest={designProgressRequest}
            onStatusesSaved={onStatusesSaved}
            error={
              touched.exclusionProgressReason && errors.exclusionProgressReason
            }
          />
        ),
      },
      ['timesheets']: {
        label: 'Timesheets',
        sublabel: null,
        component: (
          <TimesheetsStep
            values={values}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            selectedJob={selectedJob}
            timesheets={timesheets}
            team={team}
            onTimesheetChanged={onTimesheetChanged}
            loading={timesheetsLoading || teamLoading}
            error={
              touched.exclusionTimesheetsReason &&
              errors.exclusionTimesheetsReason
            }
          />
        ),
      },
      ['submit']: {
        label: 'Review & Submit',
        sublabel: null,
        component: (
          <SubmitStep
            values={values}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            selectedJob={selectedJob}
            designProgressAccountCount={designProgressAccountCount}
          />
        ),
      },
    };
  }, [
    values,
    selectedJob,
    jobs,
    timesheets,
    team,
    saving,
    isAllStepsComplete,
    editingDailyId,
    designProgressRequest,
    onStatusesSaved,
    designProgressAccountCount,
  ]);

  const steps = Object.entries(stepMap);

  const isCurrentStepValid = useMemo(() => {
    const currentStepKey = Object.keys(stepMap)[activeStep] as DailyStep;
    return getStepCompletionStatus(currentStepKey);
  }, [activeStep, stepMap, getStepCompletionStatus]);

  const isLastStep = activeStep === steps.length - 1;
  const isFirstStep = activeStep === 0;

  return (
    <Paper p="xl">
      <Stepper active={activeStep} allowNextStepsSelect>
        {steps.map(([stepKey, { label, component, sublabel }], index) => (
          <Stepper.Step
            key={stepKey}
            label={label}
            description={
              sublabel ? <Text size="xs">{sublabel}</Text> : undefined
            }
            iconSize="sm"
            completedIcon={
              getStepCompletionStatus(stepKey as DailyStep) ? '✓' : undefined
            }
            onClick={() => handleStep(index)}
            allowStepSelect
          >
            <Stack mt="lg" gap="md" w={{ base: '100%', md: '80%', xl: '65%' }}>
              {component}
            </Stack>
          </Stepper.Step>
        ))}
      </Stepper>

      {existingDaily && activeStep > 0 && (
        <Alert color="yellow">
          A daily report already exists for this job on{' '}
          {formatDate(values.date)}.{' '}
          <BasicButton
            size="xs"
            variant="light"
            color="yellow"
            onClick={loadExistingDaily}
          >
            Open Existing Daily
          </BasicButton>
        </Alert>
      )}

      <Group justify="flex-start" mt="xl">
        <BasicButton
          variant="outline"
          onClick={handleBack}
          disabled={isFirstStep}
        >
          Back
        </BasicButton>

        <CreateButton
          onClick={handleNext}
          disabled={
            !isCurrentStepValid || (existingDaily !== null && activeStep > 0)
          }
          loading={saving && isLastStep}
        >
          {isLastStep
            ? editingDailyId
              ? 'Update Daily'
              : 'Submit Daily'
            : 'Continue'}
        </CreateButton>
      </Group>
    </Paper>
  );
};
